import PageNode from "./PageNode";
import DropZone from "./DropZone";
import { Autocomplete, TextField } from "@mui/material";
import { useContext, useCallback, useEffect, useRef, useState } from "react";
import { AppContext } from "../../Contexts/Contexts";
import textFieldProps from "../../store/textfield.props";
import Viewer from "../Viewers/Viewer";
import DomUtils from "../../utils/dom.utils";
import DocumentNodeHeader from "./DocumentNodeHeader";
import { useDrag } from "react-dnd";
import Button from "../Buttons/Button";
import DeleteIcon from "../Icons/DeleteIcon";

const DocumentNode = ({
  document,
  documentIndex,
  onPageDrop,
  onSplit,
  onClassify,
  onDelete,
  selectedPageContexts,
  toggleSelectedPageContexts,
  setSelectedPageContexts,
  selected,
  onClick,
}) => {
  const { documentTypes, leftPopOut } = useContext(AppContext);
  const autocompleteRef = useRef(null);
  const [hover, setHover] = useState(false);

  const [{ isDragging }, drag] = useDrag(
    {
      type: "page",
      item: { type: "document", documentIndex },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    },
    []
  );

  const onPageDoubleClick = useCallback(
    (index, pages) => {
      leftPopOut.open(
        DomUtils.getOrangeBlackHeader(document.type, `Pages: ${pages.length}`),
        <Viewer images={pages} index={index} className={"w-half-screen"} />
      );
    },
    [document.type, leftPopOut]
  );

  const hotkeys = useCallback(
    (e) => {
      if (DomUtils.hasPopup()) return;
      if (e.ctrlKey) {
        switch (e.key) {
          case " ": // space
            autocompleteRef.current.blur();
            if (selected) {
              autocompleteRef.current.focus();
            }
            return;
          default:
            return;
        }
      }
    },
    [selected]
  );

  useEffect(() => {
    window.addEventListener("keydown", hotkeys);
    return () => {
      window.removeEventListener("keydown", hotkeys);
    };
  }, [hotkeys]);

  return (
    <div
      ref={drag}
      className="shadow rounded overflow-hidden bg-white"
      style={{
        opacity: isDragging ? 0 : 1,
        transition: "transform 0.3s ease",
        transform: `${hover ? "scale(1.01)" : "scale(1)"}`,
        border: `2px solid ${selected ? "var(--true-orange)" : "white"}`,
      }}
      onClick={onClick}
    >
      <DocumentNodeHeader
        setHover={setHover}
        number={documentIndex + 1}
        confidence={document.confidence}
        threshold={document.threshold}
        className={"w-full"}
      />
      <div className="p-1 flex-col">
        <Autocomplete
          className="shadow"
          size="small"
          openOnFocus
          options={documentTypes}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="filled"
              label="Document Type"
              inputRef={(input) => (autocompleteRef.current = input)}
              InputLabelProps={{
                style: {
                  color: "black",
                  fontWeight: "bold",
                  fontFamily: "var(--true-font-family-sans-serif)",
                  fontSize: "16px",
                },
                shrink: true,
              }}
              inputProps={{
                ...params.inputProps,
                style: {
                  fontFamily: "var(--true-font-family-serif)",
                  WebkitTextFillColor: "black",
                },
              }}
              sx={{
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                },
              }}
            />
          )}
          autoComplete
          autoHighlight
          style={{ ...textFieldProps, minWidth: "200px", flexGrow: "1" }}
          value={document.type}
          onChange={(e, value) => onClassify(document, value)}
          onFocus={() => autocompleteRef.current.select()}
        />
        <div className="flex-wrap">
          {document.pages.map((page, index) => {
            const pageContext = {
              documentIndex,
              pageIndex: index,
            };
            const dropZoneContext = {
              documentIndex,
              pageIndex: index,
              position: "left",
            };
            return (
              <div className="flex py-2" key={index}>
                <DropZone
                  accept="page"
                  context={dropZoneContext}
                  onDrop={onPageDrop}
                  onClick={() => onSplit(dropZoneContext)}
                  split={index !== 0}
                />
                <PageNode
                  context={pageContext}
                  page={page}
                  selectedPageContexts={selectedPageContexts}
                  onDrop={onPageDrop}
                  onClick={(e) => {
                    if (!e.altKey) {
                      setSelectedPageContexts(new Set());
                    }
                    toggleSelectedPageContexts(pageContext);
                  }}
                  onDoubleClick={() => onPageDoubleClick(index, document.pages)}
                />
              </div>
            );
          })}

          <DropZone
            style={{
              flexGrow: "1",
              marginTop: "8px",
            }}
            onDrop={onPageDrop}
            accept="page"
            context={{
              documentIndex,
              pageIndex: document.pages.length - 1,
              position: "right",
            }}
          />
          {document.pages.length === 0 && (
            <div className="flex items-center" style={{ marginTop: `8px` }}>
              <Button onClick={() => onDelete(documentIndex)}>
                <DeleteIcon className={"true-maroon"} />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentNode;
